<template>
  <header
    :class="['ui-topbar', { sticky: isSticky }]"
    id="header"
  >
    <!-- Left: menu (tablet/mobile) + brand -->
    <div class="ui-topbar-left">
      <button
        class="ui-icon-btn ui-topbar-burger"
        id="header-burger-menu"
        type="button"
        :aria-label="$t('header.openMenu')"
        @click="stateStoreInstance.onChange"
      >
        <i class="ph ph-list" aria-hidden="true"></i>
      </button>
      <router-link to="/" class="ui-topbar-brand" :aria-label="$t('header.home')">
        <img :src="logoSrc" alt="" />
      </router-link>
      <AccountSwitcher />
    </div>

    <!-- Right: language, theme, notifications, profile -->
    <div class="ui-topbar-right">
      <LanguageSwitcher />
      <LightDarkSwtichBtn />

      <div class="dropdown">
        <button
          class="ui-icon-btn ui-topbar-bell"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
          :aria-label="unread ? $t('header.notificationsUnread', { count: unread }) : $t('header.notifications')"
          @click="fetchNotifications"
        >
          <i class="ph ph-bell" aria-hidden="true"></i>
          <span v-if="unread" class="ui-topbar-count">{{ unread > 99 ? "99+" : unread }}</span>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu ui-notif-menu">
          <div class="ui-topbar-menu-head">
            <span>{{ $t("header.notifications") }}</span>
            <span class="ui-topbar-menu-muted">({{ unread }})</span>
            <button v-if="unread" type="button" class="ui-notif-readall" @click.stop="readAll">{{ $t("header.markAllRead") }}</button>
          </div>
          <p v-if="!notifications.length" class="ui-topbar-menu-empty">{{ $t("header.noNotifications") }}</p>
          <button
            v-for="n in notifications"
            :key="n.id"
            type="button"
            class="dropdown-item ui-notif-item"
            :class="{ 'is-unread': !n.read_at }"
            @click="openNotification(n)"
          >
            <strong>{{ n.title }}</strong>
            <span>{{ n.body }}</span>
            <small>{{ timeAgo(n.created_at) }}</small>
          </button>
        </div>
      </div>

      <div class="dropdown">
        <button
          class="ui-topbar-profile"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
        >
          <span class="ui-topbar-avatar">
            <img
              v-if="user?.pic"
              :src="user.pic"
              width="36"
              height="36"
              alt="admin"
            />
            <img
              v-else
              :src="require('@/assets/images/NoImage.png')"
              width="36"
              height="36"
              alt="admin"
            />
            <span class="ui-topbar-online" aria-hidden="true"></span>
          </span>
          <span class="ui-topbar-profile-text">
            <span class="ui-topbar-name">{{ user?.name }}</span>
            <span class="ui-topbar-role">{{ $t("header.roleClient") }}</span>
          </span>
          <i class="ph ph-caret-down ui-topbar-chevron" aria-hidden="true"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-end ui-topbar-menu">
          <div class="ui-topbar-menu-head ui-topbar-menu-user">
            <span class="text-truncate">{{ user?.name }}</span>
            <span class="ui-topbar-menu-online">{{ $t("header.onlineAccount") }}</span>
          </div>
          <router-link to="/account-settings" class="dropdown-item">
            <i class="ph ph-user-circle" aria-hidden="true"></i>
            {{ $t("header.profile") }}
          </router-link>
          <button
            type="button"
            class="dropdown-item text-danger"
            @click="handleLogout()"
          >
            <i class="ph ph-sign-out" aria-hidden="true"></i>
            {{ $t("header.logout") }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import LightDarkSwtichBtn from "./LightDarkSwtichBtn.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import AccountSwitcher from "./AccountSwitcher.vue";
import stateStore from "../../utils/store";
import api from "@/utils/axios";
import { dateLocale } from "@/i18n";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";

interface ClientNotification {
  id: number;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

const NOTIF_POLL_MS = 120000;

export default defineComponent({
  name: "MainHeader",
  components: {
    LightDarkSwtichBtn,
    LanguageSwitcher,
    AccountSwitcher,
  },
  setup() {
    const { t } = useI18n();
    const stateStoreInstance = stateStore;
    const isSticky = ref(false);
    const auth = useAuthStore();
    // Settings are fetched by App.vue when the layout is shown.
    const settingsStore = useSettingsStore();
    // computed so the header follows user updates from fetchPermissions()
    const user = computed(() => auth.user);
    const logoSrc = computed(() => settingsStore.logo || require("../../assets/images/logo.png"));

    const onScroll = () => {
      isSticky.value = window.scrollY >= 100;
    };

    // Notifications of the account (refund requests for now), same access rule as the API
    const router = useRouter();
    const notifications = ref<ClientNotification[]>([]);
    const unread = ref(0);
    const canSeeNotifications = computed(() => auth.canAccess({ staffCodes: ["REMBOURSEMENT"] }));
    let poll: number | undefined;

    async function fetchNotifications() {
      if (!auth.token || !canSeeNotifications.value) return;
      try {
        const { data } = await api.get("/api/auth/client/notifications");
        notifications.value = data.items || [];
        unread.value = Number(data.unread) || 0;
      } catch {
        // the bell stays as it is; the next poll retries
      }
    }

    async function openNotification(n: ClientNotification) {
      if (!n.read_at) {
        n.read_at = new Date().toISOString();
        unread.value = Math.max(0, unread.value - 1);
        api.post(`/api/auth/client/notifications/${n.id}/read`).catch(() => undefined);
      }
      if (n.link) router.push(n.link);
    }

    async function readAll() {
      try {
        await api.post("/api/auth/client/notifications/read-all");
        notifications.value.forEach((n) => (n.read_at = n.read_at || new Date().toISOString()));
        unread.value = 0;
      } catch {
        // kept unread; nothing else to do
      }
    }

    function timeAgo(value: string) {
      const diff = Math.max(0, (Date.now() - new Date(value).getTime()) / 1000);
      if (diff < 60) return t("header.justNow");
      if (diff < 3600) return t("header.minutesAgo", { n: Math.floor(diff / 60) });
      if (diff < 86400) return t("header.hoursAgo", { n: Math.floor(diff / 3600) });
      return new Date(value).toLocaleDateString(dateLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
    }

    onMounted(() => {
      window.addEventListener("scroll", onScroll, { passive: true });
      // Random start offset + jittered interval: many users connected at the
      // same time must not all poll (or hit the dashboard) on the same second.
      startTimer = window.setTimeout(() => {
        fetchNotifications();
        scheduleNext();
      }, 1500 + Math.random() * 2500);
    });
    let startTimer: number | undefined;
    function scheduleNext() {
      poll = window.setTimeout(() => {
        if (document.visibilityState === "visible") fetchNotifications();
        scheduleNext();
      }, NOTIF_POLL_MS * (0.8 + Math.random() * 0.4));
    }
    onBeforeUnmount(() => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(startTimer);
      window.clearTimeout(poll);
    });

    async function handleLogout() {
      await auth.logout();
      // full reload so no previous user's data stays in memory
      window.location.replace("/login");
    }
    return {
      isSticky,
      stateStoreInstance,
      user,
      logoSrc,
      notifications,
      unread,
      fetchNotifications,
      openNotification,
      readAll,
      timeAgo,
      handleLogout
    };
  },
});
</script>

<style scoped>
.ui-topbar {
  position: fixed;
  z-index: 1020;
  top: var(--ui-frame-gap);
  left: var(--ui-content-left);
  right: var(--ui-frame-gap);
  height: var(--ui-topbar-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-4);
  padding: 0 12px 0 16px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-bar);
  transition: box-shadow var(--ui-dur) var(--ui-ease), background-color var(--ui-dur) var(--ui-ease),
    left 280ms var(--ui-ease-spring), right 280ms var(--ui-ease-spring);
}
.ui-topbar.sticky {
  box-shadow: 0 14px 40px rgba(var(--ui-accent-rgb), 0.14);
}
:global(.dark) .ui-topbar.sticky {
  box-shadow: var(--ui-shadow-bar);
}

.ui-topbar-left,
.ui-topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.ui-topbar-brand {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: var(--ui-radius-md);
  transition: background-color var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-brand:hover {
  background: var(--ui-surface-sunken);
}
.ui-topbar-brand img {
  height: 48px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

/* Notification bell with pulsing dot */
.ui-topbar-bell {
  position: relative;
}
.ui-topbar-bell:hover,
.ui-topbar-bell[aria-expanded="true"] {
  color: #FFFFFF;
  background: var(--ui-accent);
  border-color: var(--ui-accent-strong);
}
.ui-topbar-count {
  position: absolute;
  top: -3px;
  right: -3px;
  display: grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: var(--ui-radius-pill);
  background: var(--ui-accent);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: var(--ui-fw-black);
  box-shadow: 0 0 0 2px var(--ui-surface);
}

/* Profile pill */
.ui-topbar-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 4px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  text-align: left;
  transition: background-color var(--ui-dur-fast) var(--ui-ease), box-shadow var(--ui-dur-fast) var(--ui-ease);
}
.ui-topbar-profile:hover,
.ui-topbar-profile[aria-expanded="true"] {
  background: var(--ui-surface);
  box-shadow: 0 6px 16px rgba(var(--ui-accent-rgb), 0.12);
}
.ui-topbar-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--ui-accent);
  box-shadow: 0 4px 12px rgba(var(--ui-accent-rgb), 0.3);
}
.ui-topbar-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.ui-topbar-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  border: 2px solid var(--ui-surface);
  background: var(--ui-success);
}
.ui-topbar-profile-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.2;
}
.ui-topbar-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-role {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-chevron {
  color: var(--ui-text-muted);
  font-size: 13px;
  transition: transform var(--ui-dur) var(--ui-ease);
}
.ui-topbar-profile[aria-expanded="true"] .ui-topbar-chevron {
  transform: rotate(180deg);
}

/* Dropdown panels */
.ui-topbar-right .dropdown {
  position: relative;
}
/* Static positioning (data-bs-display="static"): anchor under the toggle, right-aligned */
.ui-topbar-menu {
  position: absolute;
  top: 100% !important;
  right: 0 !important;
  left: auto !important;
  transform: none !important;
  min-width: 240px;
  margin-top: 10px !important;
}
.ui-topbar-menu .dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ui-topbar-menu .dropdown-item i {
  color: var(--ui-accent-text);
  font-size: 17px;
}
.ui-topbar-menu .dropdown-item.text-danger i {
  color: var(--ui-danger);
}
.ui-topbar-menu-head {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 12px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-black);
}
.ui-topbar-menu-user {
  flex-direction: column;
  gap: 2px;
}
.ui-topbar-menu-muted {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-semibold);
}
.ui-topbar-menu-online {
  color: var(--ui-success);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-topbar-menu-empty {
  margin: 0;
  padding: 14px 12px 10px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
  text-align: center;
}

/* Notifications list */
.ui-notif-menu {
  width: min(360px, calc(100vw - 32px));
  max-height: 70vh;
  overflow-y: auto;
}
.ui-notif-readall {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ui-accent-text);
  font-size: var(--ui-fs-micro);
  font-weight: var(--ui-fw-bold);
}
.ui-notif-item {
  display: flex !important;
  flex-direction: column;
  align-items: flex-start !important;
  gap: 2px !important;
  white-space: normal;
  border-left: 3px solid transparent;
}
.ui-notif-item.is-unread {
  border-left-color: var(--ui-accent);
  background: var(--ui-surface-sunken);
}
.ui-notif-item strong {
  font-size: var(--ui-fs-sm);
}
.ui-notif-item span {
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
}
.ui-notif-item small {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-micro);
}

/* Responsive */
@media (min-width: 1200px) {
  .ui-topbar-burger {
    display: none;
  }
}
@media (max-width: 767.98px) {
  .ui-topbar {
    height: 60px;
    padding: 0 8px 0 8px;
  }
  .ui-topbar-brand img {
    height: 38px;
    max-width: 120px;
  }
  .ui-topbar-profile {
    padding: 3px;
  }
  .ui-topbar-profile-text,
  .ui-topbar-chevron {
    display: none;
  }
  .ui-topbar-right {
    gap: 6px;
  }
  /* Phones: open menus as a full-width panel just below the header */
  .ui-topbar-menu {
    position: fixed !important;
    top: calc(var(--ui-frame-gap) + 60px) !important;
    left: var(--ui-frame-gap) !important;
    right: var(--ui-frame-gap) !important;
    width: auto !important;
    min-width: 0;
    max-width: none !important;
    max-height: calc(100vh - var(--ui-frame-gap) * 2 - 80px);
    overflow-y: auto;
  }
}
</style>
